import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell } from '@/components/shared/Placeholders';
import { ROUTES } from '@/constants/routes';
import { useUserAuth } from '@/context/UserAuthContext';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, clearError } = useUserAuth();
  const [mode, setMode] = useState<'email' | 'phone'>('email');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    clearError();
    try {
      await login({
        password,
        ...(mode === 'email' ? { email: identifier } : { phone: identifier }),
      });
      navigate(ROUTES.dashboard);
    } catch {
      // error handled in context
    }
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in with your email or phone number"
      loading={isLoading}
      loadingLabel="Signing in..."
      footer={
        <>
          Don&apos;t have an account?{' '}
          <Link to={ROUTES.register} className="font-semibold text-primary hover:underline">
            Register
          </Link>
        </>
      }
    >
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div className="flex rounded-xl bg-brand-surface p-1">
          {(['email', 'phone'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setMode(tab)}
              className={`flex-1 rounded-lg py-2 text-sm font-semibold capitalize transition-colors ${
                mode === tab ? 'bg-white text-brand-navy shadow-sm' : 'text-brand-muted'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <Input
          label={mode === 'email' ? 'Email address' : 'Phone number'}
          name="identifier"
          type={mode === 'email' ? 'email' : 'tel'}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder={mode === 'email' ? 'you@example.com' : '08012345678'}
          required
        />
        <Input
          label="Password"
          name="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="6–12 characters"
          required
        />

        <div className="text-right">
          <Link to={ROUTES.forgotPassword} className="text-sm font-semibold text-primary hover:underline">
            Forgot password?
          </Link>
        </div>

        <Button type="submit" fullWidth loading={isLoading} loadingText="Signing in...">
          Sign in
        </Button>
      </form>
    </AuthShell>
  );
}
